import type { AtsOfferLetterTemplateDataToFillFormQuery } from 'generated/resources/graphql'

import type { Company, Employee } from 'modules/core/hooks/useCurrentSession'

type ApplicationData = NonNullable<AtsOfferLetterTemplateDataToFillFormQuery['ats']['application']>

export interface SessionData {
  company: Company
  employee: Employee
  email: string
}

const APPLICATION_VARIABLE_MAPPING: Record<string, (data: ApplicationData) => string> = {
  candidate_first_name: (data) => data.firstName,
  candidate_last_name: (data) => data.lastName,
  candidate_full_name: (data) => data.fullName,
  candidate_email: (data) => data.email ?? '',
  candidate_phone: (data) => data.phone ?? '',
  job_title: (data) => data.jobPosting.title,
  job_contract_type: (data) => data.jobPosting.contractTypeLabel ?? '',
  job_workplace_type: (data) => data.jobPosting.workplaceTypeLabel ?? '',
  job_workplace_location: (data) => data.jobPosting.location?.name ?? '',
  legal_entity_name: (data) => data.jobPosting.legalEntity?.legalName ?? '',
  legal_entity_address: (data) => {
    const { addressLine1, addressLine2, postalCode } = data.jobPosting.legalEntity ?? {}
    return [addressLine1, addressLine2, postalCode].filter(Boolean).join(', ')
  },
  salary_amount: (data) => String(data.jobPosting.salaryFromAmountInCents ?? ''),
  start_date: (data) => data.jobPosting.startDate ?? '',
  team_name: (data) => data.jobPosting.team?.name ?? '',
}

const SESSION_VARIABLE_MAPPING: Record<string, (session: SessionData) => string> = {
  hiring_manager_name: (session) =>
    session.employee.preferredName || `${session.employee.firstName} ${session.employee.lastName}`,
  hiring_manager_email: (session) => session.email,
  company_name: (session) => session.company.name,
}

export function getSystemVariableDefaults(
  variableNames: string[],
  applicationData: ApplicationData | undefined | null,
  sessionData: SessionData
): Record<string, string> {
  return variableNames.reduce<Record<string, string>>((acc, name) => {
    const applicationMapper = APPLICATION_VARIABLE_MAPPING[name]
    if (applicationMapper && applicationData) {
      acc[name] = applicationMapper(applicationData)
      return acc
    }

    const sessionMapper = SESSION_VARIABLE_MAPPING[name]
    if (sessionMapper) {
      acc[name] = sessionMapper(sessionData)
    }

    return acc
  }, {})
}
